import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Users, ListTodo, AlertTriangle, CalendarDays, Plane, ShieldCheck, Target, Megaphone, Sparkles, ArrowRight, ClipboardCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Panel, EmptyState, StatusBadge, PriorityDot, PersonAvatar } from "@/components/common";
import { ProposalCard } from "@/components/ProposalCard";
import { api, localToday, fmtTime, fmtDate, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Kpi = ({ label, value, icon: Icon, tone, testid, to }) => (
  <Link to={to} className="bg-card border border-border rounded-xl p-4 hover:shadow-md transition-shadow block" data-testid={testid}>
    <div className="flex items-center justify-between">
      <p className="text-xs text-muted-foreground">{label}</p>
      <Icon className={`h-4 w-4 ${tone || "text-muted-foreground"}`} />
    </div>
    <p className="text-2xl sm:text-3xl font-semibold font-display tabular-nums mt-2" data-testid={`${testid}-value`}>{value}</p>
  </Link>
);

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/dashboard", { params: { date: localToday() } });
    setData(data);
  }, []);

  useEffect(() => { load().catch(() => setData(false)); }, [load]);

  if (data === null) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-72" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}</div>
        <Skeleton className="h-64" />
      </div>
    );
  }
  if (data === false) return <EmptyState icon={AlertTriangle} title="Couldn't load the dashboard" action={<Button onClick={load}>Retry</Button>} />;

  const k = data.kpis;
  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-sm text-muted-foreground">{new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</p>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mt-1">{greeting()}, {user.name?.split(" ")[0]}</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => navigate("/briefing")} data-testid="dashboard-briefing-button">Today's briefing</Button>
          <Button onClick={() => navigate("/ai-ceo?q=What needs my attention today?")} data-testid="dashboard-ask-ceo-button">
            <Sparkles className="h-4 w-4 mr-1.5" /> What needs my attention?
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Meetings today" value={k.meetings_today} icon={CalendarDays} testid="kpi-meetings-today" to="/calendar" />
        <Kpi label="Open tasks" value={k.open_tasks} icon={ListTodo} testid="kpi-open-tasks" to="/tasks" />
        <Kpi label="Overdue tasks" value={k.overdue_tasks} icon={AlertTriangle} tone={k.overdue_tasks ? "text-destructive" : ""} testid="kpi-overdue-tasks" to="/tasks" />
        <Kpi label="Pending approvals" value={k.pending_approvals + k.pending_leave} icon={ShieldCheck} tone={k.pending_approvals + k.pending_leave ? "text-[hsl(var(--warning))]" : ""} testid="kpi-pending-approvals" to="/approvals" />
        <Kpi label="Headcount" value={k.headcount} icon={Users} testid="kpi-headcount" to="/people" />
        <Kpi label="Out today" value={k.out_today} icon={Plane} testid="kpi-out-today" to="/leave" />
        <Kpi label="Onboarding" value={k.onboarding_active} icon={ClipboardCheck} testid="kpi-onboarding" to="/onboarding" />
        <Kpi label="Avg goal progress" value={`${k.goals_avg_progress}%`} icon={Target} testid="kpi-goal-progress" to="/goals" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Panel title="Today's schedule" icon={CalendarDays} className="lg:col-span-7" testid="dashboard-today-meetings"
          action={<Link to="/calendar" className="text-xs text-primary hover:underline">Open calendar</Link>}>
          {data.meetings_today.length === 0 ? (
            <EmptyState icon={CalendarDays} title="No meetings today" text="A clear day. Ask the CEO to set up time for your priorities." />
          ) : (
            <ul className="divide-y divide-border">
              {data.meetings_today.map((m) => (
                <li key={m.meeting_id} className="py-3 flex items-start gap-4" data-testid="dashboard-meeting-item">
                  <div className="w-16 shrink-0 text-sm font-mono tabular-nums">{fmtTime(m.start)}<span className="block text-xs text-muted-foreground">{fmtTime(m.end)}</span></div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{m.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{m.attendee_names?.join(", ") || "No attendees"}{m.location ? ` · ${m.location}` : ""}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Waiting for your approval" icon={ShieldCheck} className="lg:col-span-5" testid="dashboard-pending-approvals"
          action={<Link to="/approvals" className="text-xs text-primary hover:underline">View all</Link>}>
          {data.pending_proposals.length === 0 ? (
            <EmptyState icon={ShieldCheck} title="Nothing to approve" text="Proposals from the AI CEO will appear here." />
          ) : (
            <div className="space-y-3">
              {data.pending_proposals.slice(0, 3).map((p) => <ProposalCard key={p.action_id} proposal={p} compact onChange={load} />)}
            </div>
          )}
        </Panel>

        <Panel title="Overdue & due today" icon={AlertTriangle} className="lg:col-span-7" testid="dashboard-overdue-tasks"
          action={<Link to="/tasks" className="text-xs text-primary hover:underline">Task board</Link>}>
          {data.overdue_tasks.length + data.due_today.length === 0 ? (
            <EmptyState icon={ListTodo} title="Nothing overdue" text="Everything is on schedule." />
          ) : (
            <ul className="divide-y divide-border">
              {[...data.overdue_tasks.map((t) => ({ ...t, _over: true })), ...data.due_today].map((t) => (
                <li key={t.task_id} className="py-3 flex items-center gap-3" data-testid="dashboard-task-item">
                  <PersonAvatar name={t.assignee_name || "?"} className="h-7 w-7" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{t.title}</p>
                    <p className="text-xs text-muted-foreground">{t.assignee_name || "Unassigned"} · <PriorityDot priority={t.priority} /></p>
                  </div>
                  <span className={`text-xs font-medium ${t._over ? "text-destructive" : "text-[hsl(var(--warning))]"}`}>
                    {t._over ? `Overdue · ${fmtDate(t.due_date)}` : "Due today"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Leave requests" icon={Plane} className="lg:col-span-5" testid="dashboard-leave-pending"
          action={<Link to="/leave" className="text-xs text-primary hover:underline">Manage</Link>}>
          {data.pending_leave.length === 0 && data.out_today.length === 0 ? (
            <EmptyState icon={Plane} title="No pending leave" />
          ) : (
            <ul className="space-y-3">
              {data.out_today.map((l) => (
                <li key={l.leave_id} className="flex items-center justify-between gap-2 text-sm"><span>{l.employee_name} is out today</span><StatusBadge status="on_leave" /></li>
              ))}
              {data.pending_leave.map((l) => (
                <li key={l.leave_id} className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{l.employee_name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{l.leave_type} · {fmtDate(l.start_date)} – {fmtDate(l.end_date)} ({l.days}d)</p>
                  </div>
                  <StatusBadge status="pending" />
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Goals" icon={Target} className="lg:col-span-7" testid="dashboard-goals"
          action={<Link to="/goals" className="text-xs text-primary hover:underline">All goals</Link>}>
          {data.goals.length === 0 ? <EmptyState icon={Target} title="No goals yet" /> : (
            <ul className="space-y-4">
              {data.goals.slice(0, 4).map((g) => (
                <li key={g.goal_id}>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <p className="text-sm font-medium truncate">{g.title}</p>
                    <span className="text-xs tabular-nums text-muted-foreground">{g.progress}%</span>
                  </div>
                  <Progress value={g.progress} className="h-2" />
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Team announcements" icon={Megaphone} className="lg:col-span-5" testid="dashboard-announcements">
          {data.announcements.length === 0 ? <EmptyState icon={Megaphone} title="No announcements" /> : (
            <ul className="space-y-4">
              {data.announcements.map((a) => (
                <li key={a.announcement_id} data-testid="dashboard-announcement-item">
                  <p className="text-sm font-medium">{a.title}</p>
                  <p className="text-sm text-muted-foreground mt-0.5">{a.body}</p>
                  <p className="text-xs text-muted-foreground mt-1">{a.author} · {fmtDateTime(a.created_at)}</p>
                </li>
              ))}
            </ul>
          )}
          <Button variant="ghost" size="sm" className="mt-3 px-0 text-primary" onClick={() => navigate("/ai-ceo?q=Draft a short team announcement about ")} data-testid="dashboard-draft-announcement">
            Draft one with the CEO <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </Panel>
      </div>
    </div>
  );
}
